import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Search, Expand, Share, Copy, Check, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { useFrame } from '../contexts/FrameContext';
import ConfigurePanel, { BASE_STYLE_OPTIONS } from '../components/ConfigurePanel';
import OrderForm from '../components/OrderForm';
import WelcomeOverlay from '../components/WelcomeOverlay';
import { motion } from 'framer-motion';
import { propertyTokenGenerator } from '../services/tokenGenerator';
import { supabase } from '../lib/supabase';
import { updateMetaTags, generatePropertyMetaData } from '../utils/metaTags';
import { generateMissingAIStyles } from '../services/imageProcessing';
import { campaignTracking } from '../services/campaignTracking';
import { sendSlackNotification, notifyConfiguratorEntry } from '../utils/slackNotifications';
import { trackConfiguratorView, trackFunnelStep, trackConfiguratorLaunchAllPixels } from '../utils/analytics';
import { frameAvailabilityService } from '../services/frameAvailability';
import { updateWidgetForConfigurator } from '../utils/widgetSync';

const Configurator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [isMagnifyActive, setIsMagnifyActive] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [rawMousePosition, setRawMousePosition] = useState({ x: 50, y: 50 });
  const [isMouseInFrame, setIsMouseInFrame] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [frameCenter, setFrameCenter] = useState({ x: 0, y: 0 });
  const [dynamicScale, setDynamicScale] = useState(1);
  
  // Share functionality state
  const [shareUrl, setShareUrl] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareThumbnail, setShareThumbnail] = useState<string | null>(null);
  const [shareAddress, setShareAddress] = useState<string>('');
  const [shareCity, setShareCity] = useState<string>('');
  const [shareAddressLine1, setShareAddressLine1] = useState<string>('');
  const [shareMessage, setShareMessage] = useState<string>('');
  const [shareMode, setShareMode] = useState<'social' | 'copy'>('social');
  
  // Welcome overlay state
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);
  const [currentAddress, setCurrentAddress] = useState<string>('');
  const [currentWatercolorImage, setCurrentWatercolorImage] = useState<string | null>(null);
  
  // Get selectedMode from localStorage
  const [selectedMode, setSelectedMode] = useState<'home' | 'pet'>(() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  });
  
  // Use refs to prevent duplicate generation calls and track processed properties
  const generationInProgress = useRef<string | null>(null);
  const lastProcessedProperty = useRef<string | null>(null);
  const effectCleanup = useRef<(() => void) | null>(null);
  const hasTrackedView = useRef(false);
  const hasProcessedPropertyData = useRef(false);
  
  const {
    address,
    propertyImage,
    selectedFrame,
    selectedStyle,
    selectedSize,
    isOrientaAddress,
    isChapelHouse,
    styleImages,
    propertySearchId,
    setPropertyImage,
    setStyleImages,
    setPropertySearchId,
    setSelectedStyle,
    setImageSource,
    setAddress,
    imageSource
  } = useFrame();
  
  // Preload frame availability on configurator mount
  useEffect(() => {
    // Start loading frame availability in the background as soon as configurator mounts
    frameAvailabilityService.getAvailableFrameIds()
      .then(availableFrames => {
        console.log('🖼️ Preloaded frame availability in configurator:', availableFrames);
      })
      .catch(error => {
        console.warn('⚠️ Failed to preload frame availability:', error);
      });
  }, []);

  // Track configurator view on mount
  useEffect(() => {
    console.log('🎯 Configurator mounted with propertySearchId:', propertySearchId);
    console.log('🎯 Current URL search params:', location.search);
    console.log('🎯 Location state:', location.state);
    console.log('🎯 FrameContext values:', {
      propertySearchId,
      address,
      imageSource,
      hasPropertyImage: !!propertyImage,
      hasStyleImages: !!(styleImages && Object.keys(styleImages).length > 0)
    });
    
    // Check if this is a redirect from /h route to prevent double tracking
    const searchParams = new URLSearchParams(location.search);
    const isFromLandingRedirect = searchParams.get('from') === 'landing_h_redirect';
    
    if (isFromLandingRedirect) {
      console.log('🔄 Skipping configurator tracking - redirected from /h route');
      return; // Skip all tracking for this case
    }

    // Track page view for GA, Pinterest, and Facebook
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      
      // Get the current artwork URL - prioritize watercolor, then fallback to other styles
      const currentArtworkUrl = styleImages?.watercolor || 
                                styleImages?.pencil || 
                                styleImages?.oil || 
                                propertyImage || 
                                '';
      
      // Track configurator launch in widget
      window.trackPageView?.('launched_configurator', {
        imageUrl: currentArtworkUrl,  // The generated artwork URL
        artworkId: propertySearchId || 'unknown',  // Internal artwork/property ID
        mode: selectedMode,
        has_property_search_id: !!propertySearchId,
        has_images: !!(propertyImage || styleImages?.pencil || styleImages?.oil),
        image_source: imageSource
      });
      
      const hasGeneratedImages = !!(propertyImage || styleImages?.pencil || styleImages?.oil);
      
      // Try to get user email for enhanced tracking
      const getUserEmailForTracking = async () => {
        if (!propertySearchId) return undefined;
        
        try {
          const { data, error } = await supabase
            .from('property_searches')
            .select('customer_email')
            .eq('id', propertySearchId)
            .maybeSingle();
          
          if (error || !data?.customer_email) return undefined;
          return data.customer_email;
        } catch (error) {
          console.warn('Could not fetch user email for tracking:', error);
          return undefined;
        }
      };
      
      // Fire tracking with email (async to avoid blocking UI)
      getUserEmailForTracking().then(userEmail => {
        // Fire all tracking pixels (GA4, Pinterest, Facebook when available)
        trackConfiguratorLaunchAllPixels(userEmail, {
          imageSource: imageSource || undefined,
          hasGeneratedImages,
          propertyId: propertySearchId || 'unknown',
          hasAddress: !!address,
          styleImagesAvailable: Object.keys(styleImages || {}).length,
          selectedMode: selectedMode
        });
      });
      
      // Send Slack notification with image (prefer original/property image as it's always available)
      const imageToSend = propertyImage || styleImages?.watercolor || styleImages?.pencil || styleImages?.oil;
      if (imageToSend && imageSource) {
        console.log('🔔 Sending Slack configurator entry notification with image:', imageToSend);
        notifyConfiguratorEntry(
          imageSource as 'address' | 'upload' | 'webcam',
          imageToSend,
          address,
          selectedMode
        ).catch(error => {
          console.warn('Failed to send Slack notification:', error);
        });
      } else {
        console.warn('⚠️ No image available for Slack notification:', { propertyImage, styleImages, imageSource });
      }
      
      // Keep existing GA4 tracking for backward compatibility
      trackFunnelStep('Configurator Reached', 4, {
        property_id: propertySearchId || 'unknown',
        image_source: imageSource || 'unknown',
        has_address: !!address,
        style_images_available: Object.keys(styleImages || {}).length
      });

      // Update feedback widget with configurator context
      const currentImage = propertyImage || styleImages?.pencil || styleImages?.oil || styleImages?.charcoal || styleImages?.chalk;
      const allImages = {
        watercolor: propertyImage,
        pencil: styleImages?.pencil || null,
        oil: styleImages?.oil || null,
        charcoal: styleImages?.charcoal || null,
        chalk: styleImages?.chalk || null
      };
      const frameUrl = getFrameUrl(selectedFrame);
      
      // Get email from localStorage as fallback
      const storedEmail = localStorage.getItem('customer_email') || null;
      
      console.log('🚀 Configurator launching, sending to widget:', {
        selectedFrame,
        frameUrl: frameUrl.substring(0, 50) + '...',
        email: storedEmail ? '✅ Available' : '❌ Not set'
      });
      updateWidgetForConfigurator(currentImage, address, propertySearchId, allImages, frameUrl, selectedFrame, storedEmail);
    }
    
    // Determine if this is from token-based access (PropertyTokenPage) or regular flow
    const isFromToken = location.state?.fromToken || location.state?.propertyData;
    
    // Additional check: if we have a stored campaign code, this might be a refresh after token access
    const storedCampaignCode = localStorage.getItem('original_campaign_code');
    const hasStoredCampaignCode = !!storedCampaignCode;
    
    console.log('🎯 Flow detection:', {
      isFromToken,
      hasPropertySearchId: !!propertySearchId,
      locationStateKeys: location.state ? Object.keys(location.state) : 'no state',
      fromToken: location.state?.fromToken,
      hasPropertyData: !!location.state?.propertyData,
      storedCampaignCode,
      hasStoredCampaignCode,
      hasProcessedPropertyData: hasProcessedPropertyData.current
    });
    
    if (isFromToken) {
      console.log('🎯 Token-based access detected - configurator tracking handled by PropertyTokenPage');
    } else if (hasStoredCampaignCode && !location.state && !hasProcessedPropertyData.current) {
      // This might be a refresh after token-based access - check recent tracking
      // Only trigger if we haven't processed property data (to avoid double tracking after navigation)
      console.log('🎯 Refresh detected with stored campaign code - checking for recent tracking');
      
      const trackConfiguratorAccessWithCaution = async () => {
        if (!storedCampaignCode || !propertySearchId) {
          console.log('🎯 Missing required data for refresh tracking:', { storedCampaignCode, propertySearchId });
          return;
        }
        
        try {
          const { campaignTracking } = await import('../services/campaignTracking');
          
          // Get campaign info for the stored code
          let campaignInfo = null;
          try {
            campaignInfo = await campaignTracking.getCampaignPropertyInfo(storedCampaignCode);
            console.log('🎯 [DEBUG] Campaign info for stored code:', campaignInfo);
          } catch (lookupError) {
            console.log('🎯 [DEBUG] Error looking up stored campaign code:', lookupError);
          }
          
          console.log('🎯 [DEBUG] About to call trackConfiguratorAccessed with stored code...');
          
          // Try to track - the deduplication logic will prevent duplicates
          await campaignTracking.trackConfiguratorAccessed({
            propertyId: propertySearchId,
            campaignId: campaignInfo?.campaignId,
            accessCode: storedCampaignCode
          });
          
          console.log('✅ Tracked configurator access for refresh with stored campaign code:', propertySearchId, {
            campaignId: campaignInfo?.campaignId,
            accessCode: storedCampaignCode,
            isCampaignProperty: !!campaignInfo?.campaignId
          });
        } catch (error) {
          console.error('❌ Error tracking configurator access for refresh:', error);
        }
      };
      
      trackConfiguratorAccessWithCaution();
    } else if (propertySearchId) {
      // This is regular flow (landing page → address entry → configurator)
      console.log('🎯 Regular flow detected - tracking configurator access');
      console.log('🎯 Starting configurator tracking for regular flow with propertySearchId:', propertySearchId);
      
      const trackConfiguratorAccess = async () => {
        try {
          console.log('🎯 [DEBUG] Importing campaignTracking...');
          const { campaignTracking } = await import('../services/campaignTracking');
          
          console.log('🎯 [DEBUG] Looking up campaign info for propertySearchId:', propertySearchId);
          
          // For regular flow, try to get any campaign info if this property has a token
          let campaignInfo = null;
          try {
            const { data: propertyData } = await supabase
              .from('property_searches')
              .select('property_token')
              .eq('id', propertySearchId)
              .maybeSingle();
            
            console.log('🎯 [DEBUG] Property data lookup result:', propertyData);
            
            if (propertyData?.property_token) {
              console.log('🎯 [DEBUG] Found property_token, checking bridge:', propertyData.property_token);
              const { propertyTokenGenerator } = await import('../services/tokenGenerator');
              const propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(propertyData.property_token);
              if (propertyResult) {
                console.log('🎯 [DEBUG] Bridge result found, getting campaign info...');
                campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertyResult.accessCode || propertyData.property_token);
              } else {
                // Fallback to direct lookup if bridge doesn't work
                console.log('🎯 [DEBUG] Bridge failed, trying direct lookup...');
                campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertyData.property_token);
              }
            } else {
              console.log('🎯 [DEBUG] No property_token found for this property');
            }
          } catch (lookupError) {
            console.log('🎯 [DEBUG] Error during campaign info lookup:', lookupError);
          }
          
          console.log('🎯 [DEBUG] Final campaign info:', campaignInfo);
          console.log('🎯 [DEBUG] About to call trackConfiguratorAccessed...');
          
          await campaignTracking.trackConfiguratorAccessed({
            propertyId: propertySearchId,
            campaignId: campaignInfo?.campaignId,
            accessCode: campaignInfo?.accessCode
          });
          
          console.log('✅ Tracked configurator access for regular flow:', propertySearchId, {
            campaignId: campaignInfo?.campaignId,
            accessCode: campaignInfo?.accessCode,
            isCampaignProperty: !!campaignInfo?.campaignId
          });
        } catch (error) {
          console.error('❌ Error tracking configurator access for regular flow:', error);
        }
      };
      
      trackConfiguratorAccess();
    } else {
      console.log('�� No tracking needed');
      console.log('🎯 Debug info - why no tracking:', {
        propertySearchId: propertySearchId || 'undefined/null',
        isFromToken,
        hasStoredCampaignCode,
        hasLocationState: !!location.state,
        hasProcessedPropertyData: hasProcessedPropertyData.current,
        reason: !propertySearchId ? 'No propertySearchId' : 
                isFromToken ? 'Token-based access (handled elsewhere)' :
                hasStoredCampaignCode && !location.state && !hasProcessedPropertyData.current ? 'Would refresh track but condition not met' :
                hasStoredCampaignCode && hasProcessedPropertyData.current ? 'Stored code but property data already processed' :
                'Other condition'
      });
    }
  }, [propertySearchId]); // Add propertySearchId as dependency to trigger when it becomes available

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get the first available style
  const getFirstAvailableStyle = (propertyData: any) => {
    if (propertyData.watercolor_image_url) return 'watercolor';
    if (propertyData.pencil_image_url) return 'pencil';
    if (propertyData.oil_image_url) return 'oil';
    if (propertyData.charcoal_image_url) return 'charcoal';
    if (propertyData.chalk_image_url) return 'chalk';
    return 'watercolor'; // Default fallback
  };

  // Helper function to check if all 3 main styles are available
  const checkMissingStyles = (propertyData: any) => {
    const requiredStyles = ['watercolor', 'pencil', 'oil'];
    const availableStyles: Record<string, any> = {
      watercolor: propertyData.watercolor_image_url,
      pencil: propertyData.pencil_image_url,
      oil: propertyData.oil_image_url
    };
    
    return requiredStyles.filter(style => !availableStyles[style]);
  };

  // Effect to handle property data from navigation state
  useEffect(() => {
    const handlePropertyData = async () => {
      if (!location.state?.propertyData) {
        console.log('🔍 No property data in location state');
        return;
      }

      const { propertyData } = location.state;
      const logPrefix = `🎨 [React:${propertyData.id.slice(-8)}]`;
      
      // Add HMR detection
      const isHMR = import.meta.hot?.data?.lastProcessedId === propertyData.id;
      if (isHMR) {
        console.log(`${logPrefix} 🔄 HMR detected - same property ID as previous mount`);
      }
      
      // Store the ID for HMR detection (safely)
      if (import.meta.hot && import.meta.hot.data) {
        try {
          (import.meta.hot.data as any).lastProcessedId = propertyData.id;
        } catch {
          // Fallback if HMR data is read-only
        }
      }
      
      // Check if we've already processed this exact property
      if (lastProcessedProperty.current === propertyData.id) {
        console.log(`${logPrefix} 🚫 Property already processed - SKIPPING ${isHMR ? '(HMR)' : ''}`);
        return;
      }

      console.log(`${logPrefix} Processing property data - Full ID: ${propertyData.id}`);
      
      // Mark this property as processed IMMEDIATELY
      lastProcessedProperty.current = propertyData.id;
      hasProcessedPropertyData.current = true; // Set the flag
      console.log(`${logPrefix} 🔒 Setting React ref generation flag`);
      console.log(`${logPrefix} 🔒 Set hasProcessedPropertyData flag to prevent mount effect double tracking`);
      generationInProgress.current = propertyData.id;
      
      // Process property data - removed duplicate tracking as it's handled in the mount useEffect
      console.log(`${logPrefix} Processing property data...`);
      try {
        // Get access code and campaign info from location state for other purposes
        const accessCode = location.state?.accessCode || location.state?.token;
        
        // Get campaign info if this is a campaign property (for Slack notifications)
        let campaignInfo = null;
        if (accessCode) {
          campaignInfo = await campaignTracking.getCampaignPropertyInfo(accessCode);
        }
        
        console.log(`${logPrefix} ✅ Property data processed`, {
          propertyId: propertyData.id,
          campaignId: campaignInfo?.campaignId || propertyData.campaign_id,
          accessCode: accessCode,
          isCampaignProperty: !!(campaignInfo?.campaignId || propertyData.campaign_id)
        });
        
        // 📢 SEND SLACK NOTIFICATION FOR CONFIGURATOR ACCESS
        if (accessCode && (campaignInfo?.campaignId || propertyData.campaign_id)) {
          // Send notification every time configurator is accessed via bridge
          const isFromBridge = location.state?.fromBridge;
          
          console.log(`${logPrefix} 🔍 Notification check:`, {
            accessCode,
            campaignId: campaignInfo?.campaignId || propertyData.campaign_id,
            fromBridge: location.state?.fromBridge,
            wasExisting: location.state?.wasExisting,
            isFromBridge,
            currentPropertyId: propertyData.id
          });
          
          if (isFromBridge) {
            console.log(`${logPrefix} 📢 Sending Slack notification for configurator access via bridge...`);
            try {
              const addressDisplay = propertyData.address || 
                                   (propertyData.address_line_1 ? 
                                     `${propertyData.address_line_1}, ${propertyData.city}, ${propertyData.state}` : 
                                     'Campaign Property');
              
              await sendSlackNotification({
                type: 'image_generation',  // Using existing type since it's about configurator access
                source: 'address',  // Campaign properties are typically address-based
                address: addressDisplay,
                additionalInfo: `🎯 Campaign Property Access (Code: ${accessCode}) | Mode: ${selectedMode === 'pet' ? '🐾 Pet' : '🏠 Home'}`
              });
              
              console.log(`${logPrefix} ✅ Successfully sent Slack notification for configurator access`);
            } catch (slackError) {
              console.warn(`${logPrefix} ⚠️ Failed to send Slack notification:`, slackError);
              // Continue anyway - Slack notifications shouldn't block the user experience
            }
          } else {
            console.log(`${logPrefix} 🔇 Skipping Slack notification - not accessed via bridge`);
          }
        }
        
      } catch (trackingError) {
        console.warn(`${logPrefix} ⚠️ Failed to track configurator accessed event:`, trackingError);
        // Continue anyway - tracking shouldn't block the user experience
      }
      
      // Process property data
      const token = location.state?.token;
        
        // Maintain meta tags for social sharing if loaded from token
        if (token) {
          try {
            const metaData = generatePropertyMetaData(propertyData, token);
            updateMetaTags(metaData);
            console.log('Maintained meta tags for configurator from token:', metaData);
          } catch (metaError) {
            console.warn('Failed to maintain meta tags in configurator:', metaError);
          }
        }
        
        // Set the property search ID
        setPropertySearchId(propertyData.id);
        
        // Set up image source and address for bridge properties (campaign properties)
        // This is essential for OrderForm checkout/stripe notifications to work
        const isFromBridge = location.state?.fromBridge || location.state?.accessCode;
        if (isFromBridge) {
          console.log(`${logPrefix} 🎯 Setting up FrameContext for bridge property...`);
          
          // Set image source as 'address' for campaign properties
          setImageSource('address');
          
          // Build full address from property data
          const fullAddress = propertyData.address || 
                             (propertyData.address_line_1 ? 
                               `${propertyData.address_line_1}, ${propertyData.city}, ${propertyData.state}` : 
                               '');
          
          if (fullAddress) {
            setAddress(fullAddress);
            console.log(`${logPrefix} ✅ Set FrameContext - imageSource: 'address', address: '${fullAddress}'`);
          } else {
            console.warn(`${logPrefix} ⚠️ No address found for bridge property`);
          }
        }
        
        // Set the primary image (watercolor is stored separately as propertyImage)
        const primaryImage = propertyData.watercolor_image_url || propertyData.master_image_url;
        if (primaryImage) {
          setPropertyImage(primaryImage);
        }
        
        // Set style images (excluding watercolor as it's handled separately)
        const initialStyleImages = {
          pencil: propertyData.pencil_image_url || null,
          oil: propertyData.oil_image_url || null,
          charcoal: propertyData.charcoal_image_url || null,
          chalk: propertyData.chalk_image_url || null
        };
        setStyleImages(initialStyleImages);
        
        // Set the default style to the first available one
        const firstAvailableStyle = getFirstAvailableStyle(propertyData);
        setSelectedStyle(firstAvailableStyle);
        
      console.log(`${logPrefix} Property data loaded successfully:`, {
          id: propertyData.id,
          primaryImage,
          styleImages: {
            watercolor: propertyData.watercolor_image_url,
            pencil: propertyData.pencil_image_url,
            oil: propertyData.oil_image_url,
            charcoal: propertyData.charcoal_image_url,
            chalk: propertyData.chalk_image_url
          },
          defaultStyle: firstAvailableStyle
        });
      
      // Set current data for welcome overlay
      setCurrentAddress(propertyData.address || '');
      setCurrentWatercolorImage(propertyData.watercolor_image_url || null);
        
        // Check for missing AI styles and generate them
        const missingStyles = checkMissingStyles(propertyData);
        if (missingStyles.length > 0) {
          console.log(`${logPrefix} Missing AI styles detected:`, missingStyles);
          
          // Show welcome overlay if coming from bridge function (scraped properties) and has missing styles
          const isFromBridge = location.state?.fromBridge || location.state?.accessCode;
          if (isFromBridge && propertyData.watercolor_image_url) {
            console.log(`${logPrefix} 👋 Showing welcome overlay - from bridge with missing styles`);
            setShowWelcomeOverlay(true);
          }
          
          // Double-check if generation is already in progress for this property
          if (generationInProgress.current === propertyData.id) {
            console.log(`${logPrefix} 🚫 React ref shows generation already in progress - BLOCKING`);
            return;
          }
        } else {
          // All 3 styles exist - skip popup and go straight to configurator
          const isFromBridge = location.state?.fromBridge || location.state?.accessCode;
          if (isFromBridge) {
            console.log(`${logPrefix} ✅ All 3 image styles exist - skipping welcome overlay, going straight to configurator`);
          }
        }
        
        // Only proceed with generation if there are missing styles
        if (missingStyles.length > 0) {
          // For token-based properties, we should always generate missing styles
          // unless the property was explicitly created with single image mode
          // (which would be indicated by image_source being set and only watercolor available)
          const wasCreatedWithSingleImageMode = propertyData.image_source && 
                                               propertyData.watercolor_image_url && 
                                               !propertyData.pencil_image_url && 
                                               !propertyData.oil_image_url;
          
          // Only skip generation if this was explicitly created as single image mode
          if (!wasCreatedWithSingleImageMode) {
            // Use the master image as the primary source, fallback to generated images
            const sourceImage = propertyData.selected_image_url || 
                              propertyData.master_image_url || 
                              propertyData.watercolor_image_url || 
                              propertyData.pencil_image_url || 
                              propertyData.oil_image_url;
            
            if (sourceImage) {
            console.log(`${logPrefix} Starting automatic generation of missing styles for token-based property...`, {
                sourceImage,
                usingSelectedImage: !!propertyData.selected_image_url,
                usingMasterImage: !propertyData.selected_image_url && !!propertyData.master_image_url
              });
            
            // Mark generation as in progress BEFORE calling the function
            console.log(`${logPrefix} 🔒 Setting React ref generation flag`);
            generationInProgress.current = propertyData.id;
            
            // Start generation and clear the flag when done
              generateMissingAIStyles(
                propertyData.id,
                sourceImage,
                {
                  watercolor: propertyData.watercolor_image_url,
                  pencil: propertyData.pencil_image_url,
                  oil: propertyData.oil_image_url
                },
                setStyleImages,
                setPropertyImage
            ).finally(() => {
              // Clear the generation flag when done
              console.log(`${logPrefix} �� Clearing React ref generation flag`);
              generationInProgress.current = null;
            });
            } else {
            console.warn(`${logPrefix} No source image available for generating missing styles`);
            }
          } else {
          console.log(`${logPrefix} Property was created with single image mode - skipping additional style generation`);
          }
        }
        
        // Clear the location state to prevent reloading on refresh
        navigate('/configure', { replace: true });
    };
    
    // Set up cleanup function
    effectCleanup.current = () => {
      console.log('🧹 Cleaning up property data effect');
    };
    
    handlePropertyData();
    
    // Cleanup function
    return () => {
      if (effectCleanup.current) {
        effectCleanup.current();
      }
    };
  }, [location.state?.propertyData?.id]); // Only depend on the property ID, not the entire state

  // Reset processing flag when location state changes to allow new properties
  useEffect(() => {
    lastProcessedProperty.current = null; // Reset for new property
    hasProcessedPropertyData.current = false; // Reset for new property
  }, [location.state?.propertyData?.id]);

  const getFrameUrl = (frameId: string) => {
    switch (frameId) {
      case 'gold':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png';
      case 'mocca':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png';
      case 'natural':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png';
      case 'white':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png';
      case 'black':
      default:
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png';
    }
  };

  // Get the appropriate image based on the selected style - prioritize generated images
  const getStyleImage = () => {
    // First priority: AI-generated images
    if (styleImages) {
      if (selectedStyle === 'watercolor' && propertyImage) {
        return propertyImage;
      } else if (selectedStyle === 'pencil' && styleImages.pencil) {
        return styleImages.pencil;
      } else if (selectedStyle === 'oil' && styleImages.oil) {
        return styleImages.oil;
      } else if (selectedStyle === 'charcoal' && styleImages.charcoal) {
        return styleImages.charcoal;
      } else if (selectedStyle === 'chalk' && styleImages.chalk) {
        return styleImages.chalk;
      }
    }

    // Second priority: Get the first available generated image (any style)
    if (propertyImage) {
      return propertyImage;
    } else if (styleImages?.pencil) {
      return styleImages.pencil;
    } else if (styleImages?.oil) {
      return styleImages.oil;
    } else if (styleImages?.charcoal) {
      return styleImages.charcoal;
    } else if (styleImages?.chalk) {
      return styleImages.chalk;
    }

    // Third priority: Special case for Chapel Hill address
    if (isChapelHouse) {
      switch (selectedStyle) {
        case 'watercolor':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Watercolor.png';
        case 'pencil':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Pencil.png';
        case 'oil':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Oil.png';
        default:
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Watercolor.png';
      }
    }

    // Last resort: empty string (no image to show)
    return '';
  };

  const currentImage = getStyleImage();

  // Get the current scale factor for the image based on selected size and screen size
  const getImageScaleFactor = () => {
    // Determine screen size category using reactive windowWidth
    const isXL = windowWidth >= 1280;
    const isLG = windowWidth >= 1024;
    const isMD = windowWidth >= 768;
    
    // Get the scale factor based on selected size and screen size
    if (selectedSize === 'XS') {
      if (isXL) return 0.55;
      if (isLG) return 0.50;
      if (isMD) return 0.48;
      return 0.45;
    } else if (selectedSize === 'S') {
      if (isXL) return 0.75;
      if (isLG) return 0.65;
      if (isMD) return 0.6;
      return 0.55;
    } else if (selectedSize === 'M') {
      if (isXL) return 0.855;
      if (isLG) return 0.765;
      if (isMD) return 0.75;
      return 0.7;
    } else { // size L
      if (isXL) return 1.0;
      if (isLG) return 0.95;
      if (isMD) return 0.9;
      return 0.85;
    }
  };

  // Calculate the magnifying glass background size
  const getMagnifyBackgroundSize = () => {
    // Since we're now properly mapping coordinates to the full image,
    // we can use a consistent magnification level
    return '600%';
  };

  // Calculate the correct background position for the magnifying glass
  const getMagnifyBackgroundPosition = () => {
    // We need to map the raw position to the scaled image coordinates
    
    // First, convert raw position to frame content coordinates (account for 20% frame border)
    const frameContentX = ((rawMousePosition.x - 20) / 60) * 100; // Map from frame to content area
    const frameContentY = ((rawMousePosition.y - 20) / 60) * 100;
    
    // Then account for the image scaling within the content area
    const scaleFactor = getImageScaleFactor();
    const imageMargin = (1 - scaleFactor) / 2; // Margin on each side due to scaling
    
    // Map to the actual image coordinates considering the scaling
    const imageX = (frameContentX / 100 - imageMargin) / scaleFactor;
    const imageY = (frameContentY / 100 - imageMargin) / scaleFactor;
    
    // Convert back to percentage and clamp to valid range
    const finalX = Math.max(0, Math.min(1, imageX)) * 100;
    const finalY = Math.max(0, Math.min(1, imageY)) * 100;
    
    return `${finalX}% ${finalY}%`;
  };

  // Handle mouse movement for magnifying effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMagnifyActive) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Store raw position for background positioning
    setRawMousePosition({ x, y });
    
    // Account for the frame content area (inset-[20%] = 20% margin on all sides)
    // The actual image area is within the 20%-80% range of the frame
    const frameContentX = Math.max(20, Math.min(80, x));
    const frameContentY = Math.max(20, Math.min(80, y));
    
    // Convert to coordinates within the image content area (0-100% within the 60% content area)
    const imageX = ((frameContentX - 20) / 60) * 100;
    const imageY = ((frameContentY - 20) / 60) * 100;
    
    // Further adjust for the image scaling within the content area
    const scaleFactor = getImageScaleFactor();
    const scaledImageBounds = (1 - scaleFactor) / 2; // How much empty space on each side
    
    // Clamp to the actual scaled image bounds
    const finalX = Math.max(scaledImageBounds * 100, Math.min((1 - scaledImageBounds) * 100, imageX));
    const finalY = Math.max(scaledImageBounds * 100, Math.min((1 - scaledImageBounds) * 100, imageY));
    
    setMousePosition({ x: finalX, y: finalY });
  };

  // Handle touch movement for mobile devices
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMagnifyActive) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    // Store raw position for background positioning
    setRawMousePosition({ x, y });
    
    // Account for the frame content area (inset-[20%] = 20% margin on all sides)
    // The actual image area is within the 20%-80% range of the frame
    const frameContentX = Math.max(20, Math.min(80, x));
    const frameContentY = Math.max(20, Math.min(80, y));
    
    // Convert to coordinates within the image content area (0-100% within the 60% content area)
    const imageX = ((frameContentX - 20) / 60) * 100;
    const imageY = ((frameContentY - 20) / 60) * 100;
    
    // Further adjust for the image scaling within the content area
    const scaleFactor = getImageScaleFactor();
    const scaledImageBounds = (1 - scaleFactor) / 2; // How much empty space on each side
    
    // Clamp to the actual scaled image bounds
    const finalX = Math.max(scaledImageBounds * 100, Math.min((1 - scaledImageBounds) * 100, imageX));
    const finalY = Math.max(scaledImageBounds * 100, Math.min((1 - scaledImageBounds) * 100, imageY));
    
    setMousePosition({ x: finalX, y: finalY });
  };

  const handleMouseEnter = () => {
    if (isMagnifyActive) setIsMouseInFrame(true);
  };

  const handleMouseLeave = () => {
    setIsMouseInFrame(false);
  };

  const handleTouchStart = () => {
    if (isMagnifyActive) setIsMouseInFrame(true);
  };

  const handleTouchEnd = () => {
    setIsMouseInFrame(false);
  };

  const handleExpandClick = () => {
    // Track fullscreen toggle in widget
    window.trackInteraction?.('fullscreen_toggled', {
      action: 'opened',
      current_style: selectedStyle,
      current_frame: selectedFrame,
      mode: selectedMode
    });
    
    // Calculate dynamic scale first
    const scale = calculateFullscreenScale();
    setDynamicScale(scale);
    
    // Find the frame container element
    const frameContainer = document.querySelector('[data-frame-container]');
    if (frameContainer) {
      const rect = frameContainer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setFrameCenter({ x: centerX, y: centerY });
    }
    setShowFullscreen(true);
  };

  // Calculate dynamic scale for fullscreen viewer
  const calculateFullscreenScale = () => {
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - 100; // 50px top + 50px bottom padding
    const imageHeight = 1024; // Fixed image height
    const containerHeight = imageHeight + 160; // Image + 80px margin top/bottom
    
    const scale = availableHeight / containerHeight;
    return Math.min(scale, 1); // Don't scale up beyond 100%
  };

  // Get texture URL based on art style
  const getTextureUrl = (artStyle: string) => {
    switch (artStyle.toLowerCase()) {
      case 'oil':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Somerset_texture2.jpg';
      case 'pencil':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Hot%20press_texture.jpg';
      case 'watercolor':
      default:
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Textured_paper-2.png';
    }
  };

  // Get texture filter values for enhanced appearance
  const getTextureFilters = (artStyle: string) => {
    switch (artStyle.toLowerCase()) {
      case 'oil':
        // No effects applied
        return 'none';
      case 'pencil':
      case 'watercolor':
      default:
        return 'none';
    }
  };

  // Get texture blend mode for enhanced appearance
  const getTextureBlendMode = (artStyle: string) => {
    switch (artStyle.toLowerCase()) {
      case 'oil':
        // No blend mode effects
        return 'normal';
      case 'pencil':
      case 'watercolor':
      default:
        return 'normal';
    }
  };

  // Get contextual filter effects for fullscreen zoom based on art style
  const getFullscreenFilters = (artStyle: string) => {
    switch (artStyle.toLowerCase()) {
      case 'oil':
      case 'watercolor':
        // Keep the original effects for oil and watercolor
        return 'brightness(1.2) saturate(0.75)';
      case 'pencil':
        // 7.5% brightness boost for pencil, keep saturation reduction
        return 'brightness(1.075) saturate(0.75)';
      default:
        // Default to oil/watercolor effects
        return 'brightness(1.2) saturate(0.75)';
    }
  };

  // Share functionality
  const handleShareClick = async () => {
    if (!propertySearchId) {
      console.warn('No property search ID available for sharing');
      return;
    }
    
    // Track share modal launch in widget
    window.trackInteraction?.('launched_share_modal', {
      mode: selectedMode,
      current_style: selectedStyle,
      current_frame: selectedFrame,
      current_size: selectedSize
    });

    try {
      // Get the watercolor image (default to watercolor, then fallback to current style)
      const thumbnailImage = propertyImage || getStyleImage();
      setShareThumbnail(thumbnailImage);

      // Fetch the property token and address data from the database
      const { data, error } = await supabase
        .from('property_searches')
        .select('property_token, address, city, address_line_1, zip_code, image_source')
        .eq('id', propertySearchId)
        .single();

      if (error) {
        console.error('Error fetching property data:', error);
        return;
      }
      
      // Check if we have a valid token (either address-based or temporary)
      let defaultMessage = '';
      
      if (data?.property_token) {
        // Use existing token (works for both address-based and temporary tokens)
        const directUrl = propertyTokenGenerator.generateDirectUrl(data.property_token);
        // Add preview parameter for better iMessage compatibility
        const shareUrlToUse = `${directUrl}?preview=true`;
        setShareUrl(shareUrlToUse);
        setShareAddress(data.address || '');
        setShareCity(data.city || '');
        setShareAddressLine1(data.address_line_1 || data.address || '');
        
        // Set default message based on context
        if (data.address && data.address_line_1 && data.address_line_1.toLowerCase() !== 'property') {
          // Has real address - use fun message
          defaultMessage = `${data.address_line_1} never looked so good`;
        } else {
          // No real address - use generic message for both modes
          defaultMessage = 'Check out this nice illustration I just made from a picture';
        }
      } else {
        // No token exists - try to generate one
        if (data?.address && data?.zip_code) {
          // Generate address-based token
          const newToken = await propertyTokenGenerator.generateToken(
            data.address, 
            data.zip_code
          );
          
          await propertyTokenGenerator.saveTokenToProperty(propertySearchId, newToken);
          
          const directUrl = propertyTokenGenerator.generateDirectUrl(newToken);
          const shareUrlToUse = `${directUrl}?preview=true`;
          setShareUrl(shareUrlToUse);
          setShareAddress(data.address);
          setShareCity(data.city || '');
          setShareAddressLine1(data.address_line_1 || data.address);
          
          // Has real address - use fun message
          const addressLine = data.address_line_1 || data.address;
          defaultMessage = `${addressLine} never looked so good`;
        } else {
          // Generate temporary token for image uploads without address
          const tempToken = await propertyTokenGenerator.generateTemporaryToken();
          
          await propertyTokenGenerator.saveTokenToProperty(propertySearchId, tempToken);
          
          const directUrl = propertyTokenGenerator.generateDirectUrl(tempToken);
          const shareUrlToUse = `${directUrl}?preview=true`;
          setShareUrl(shareUrlToUse);
          setShareAddress('');
          setShareCity('');
          setShareAddressLine1('');
          
          // No real address - use generic message for both modes
          defaultMessage = 'Check out this nice illustration I just made from a picture';
        }
      }

      setShareMessage(defaultMessage);
      setShareMode('social'); // Reset to social mode when opening
      setCopySuccess(false); // Reset success states
      setShareSuccess(false);
      
      // Always show the rich preview modal
      setShowShareModal(true);
    } catch (error) {
      console.error('Error generating share URL:', error);
    }
  };

  const handleCopyUrl = async () => {
    try {
      // Just copy the URL
      await navigator.clipboard.writeText(shareUrl);
      
      // Track share completion in widget
      window.trackInteraction?.('share_completed', {
        share_method: 'copy_link',
        mode: selectedMode,
        has_address: !!shareAddressLine1 && shareAddressLine1.toLowerCase() !== 'property'
      });
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      // If native share isn't available, fall back to copying message + URL
      try {
        const textToCopy = `${shareMessage}\n\n${shareUrl}`;
        await navigator.clipboard.writeText(textToCopy);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
      return;
    }

    try {
      // For social sharing, always use the personalized message
      // Title can be first line of message or a simple default
      const title = shareMessage ? shareMessage.split('\n')[0] : 'Frame My Home';

      const shareData: ShareData = {
        title: title,
        text: shareMessage, // Always use personalized message for social
        url: shareUrl
      };

      console.log('📱 Native share data:', shareData);
      console.log('📱 Share message:', shareMessage);
      console.log('📱 Share address line 1:', shareAddressLine1);

      await navigator.share(shareData);
      
      // Track share completion in widget
      window.trackInteraction?.('share_completed', {
        share_method: 'social',
        mode: selectedMode,
        has_address: !!shareAddressLine1 && shareAddressLine1.toLowerCase() !== 'property',
        has_custom_message: !!shareMessage
      });
      
      setShareSuccess(true);
      setTimeout(() => {
        setShareSuccess(false);
        setShowShareModal(false);
      }, 1500);
    } catch (err: any) {
      // User cancelled or share failed
      if (err.name !== 'AbortError') {
        console.error('Failed to share:', err);
      }
    }
  };

  // Handle welcome overlay dismiss
  const handleWelcomeOverlayConfirm = () => {
    setShowWelcomeOverlay(false);
  };

  return (
    <div className="fixed inset-0 bg-[#F5ECE1] overflow-hidden flex flex-col">
      {/* Global Promotional Banner */}
      <div className="w-full bg-black text-white py-2 px-4 text-center z-[250] flex-shrink-0">
        <p className="text-xs font-medium">
          Limited offer! Order now and claim a 20% discount.
        </p>
      </div>

      {/* Main Content Container */}
      <div className="flex-1 relative overflow-hidden">
        {/* Configure Panel */}
      <ConfigurePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        onShowOrderForm={setShowOrderForm}
        reduceZIndex={showOrderForm}
        onShare={handleShareClick}
      />

      {/* Plant Decoration */}
      <img
        src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//plant-leafs.png"
        alt="Plant Leaves"
        className={`fixed top-[15vh] w-64 pointer-events-none hidden md:block transform -scale-x-100 transition-all duration-300 ${showOrderForm ? 'z-10' : 'z-[100]'}`}
        style={{
          right: isMagnifyActive ? '-300px' : '0px'
        }}
      />

      {/* Logo */}
      <motion.div 
        className={`fixed left-1/2 top-8 md:hidden ${isSidePanelOpen ? 'opacity-0' : 'opacity-100'} ${showOrderForm ? 'z-10' : 'z-50'}`}
        animate={{
          x: 0,
          translateX: "-50%"
        }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 40,
          opacity: { duration: 0.2 }
        }}
      >
        <img 
          src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
          alt="Framemyhome"
          className="h-10 md:h-12 max-[479px]:h-8"
        />
      </motion.div>

        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
        className={`fixed ${isSidePanelOpen ? 'top-[50px] max-[479px]:top-[50px] md:top-[60px]' : 'top-[10px] max-[479px]:top-[10px] min-[480px]:top-[20px]'} right-4 md:right-8 z-[202] p-2 rounded-full transition-all duration-300 bg-white/80 text-gray-700 hover:bg-white hover:shadow-md max-[479px]:scale-80 max-[479px]:p-[6px] ${showOrderForm ? 'z-10' : 'z-[201]'}`}
          title="Close configurator"
        >
          <X className="w-5 h-5 max-[479px]:w-4 max-[479px]:h-4" />
        </button>

      {/* Magnifying glass icon - positioned to avoid conflicts */}
      <button
        onClick={() => setIsMagnifyActive(!isMagnifyActive)}
        className={`fixed top-8 z-[202] p-2 rounded-full transition-all duration-300 hidden ${
          isSidePanelOpen && windowWidth >= 768 ? 'left-[350px]' : 'left-4 md:left-8'
        } ${
          isMagnifyActive 
            ? 'bg-black text-white shadow-lg' 
            : 'bg-white/80 text-gray-700 hover:bg-white hover:shadow-md'
        }`}
        title={isMagnifyActive ? 'Disable magnify' : 'Enable magnify'}
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Expand/Fullscreen icon */}
      <button
        onClick={handleExpandClick}
        className={`fixed ${isSidePanelOpen ? 'top-[50px] max-[479px]:top-[50px] md:top-[60px]' : 'top-[10px] max-[479px]:top-[10px] min-[480px]:top-[20px]'} z-[202] px-3 py-2 rounded-full transition-all duration-300 flex items-center gap-2 ${
          isSidePanelOpen && windowWidth >= 768 ? 'left-[350px]' : 'left-4 md:left-8'
        } bg-white/80 text-gray-700 hover:bg-white hover:shadow-md max-[479px]:scale-80 max-[479px]:px-2 max-[479px]:py-[6px] max-[479px]:gap-[6px]`}
        title="View fullscreen"
      >
        <Expand className="w-5 h-5 max-[479px]:w-4 max-[479px]:h-4" />
        <span className="text-sm font-medium max-[479px]:text-xs">Zoom</span>
      </button>

      {/* Back Button */}
      {!isSidePanelOpen && (
        <button
          onClick={() => setIsSidePanelOpen(true)}
          className={`fixed top-8 left-16 md:left-20 text-sm text-gray-700 hover:text-gray-900 transition-colors ${showOrderForm ? 'z-10' : 'z-[201]'}`}
        >
          <X className="w-6 h-6" />
        </button>
      )}

      {/* Main Layout */}
      <div className="flex flex-col h-screen">
          {/* Mobile Logo */}
          <div className="md:hidden flex justify-center mt-8 max-[479px]:mt-[20px]">
            <img 
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
              alt="Framemyhome"
              className="h-10 max-[479px]:h-8"
            />
          </div>
        
        <motion.div
          className="flex flex-1 flex-col md:flex-row md:items-center mt-4 md:mt-0"
          animate={{
            scale: isSidePanelOpen && windowWidth >= 768 ? 0.8 : 1,
            x: isSidePanelOpen && windowWidth >= 768 ? "200px" : 0,
            transformOrigin: "left center"
          }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 40
          }}
        >
          {/* Main Content Area */}
          <div className="w-full flex-1 flex items-start md:items-center justify-center pt-0 pb-[15vh] md:pb-0 px-4 mx-0">
            {/* Frame Container with Dimensions */}
            <div 
              className="relative w-full max-w-[90vw] aspect-[4/3] lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl md:mt-0"
              data-frame-container
              style={{
                '--offset-base': 'clamp(-100px, calc(260px - 35vh), 190px)',
                '--tablet-offset': 'max(0px, min(75px, calc((100vw - 480px) * 75 / 288)))',
                marginTop: 'calc(var(--offset-base) * -1 - var(--tablet-offset))'
              } as React.CSSProperties}
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchMove={handleTouchMove}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Magnifying glass overlay */}
              {isMagnifyActive && isMouseInFrame && currentImage && (
                <div
                  className="absolute w-60 h-60 md:w-96 md:h-96 border-4 border-white rounded-full shadow-xl pointer-events-none z-[200] overflow-hidden"
                  style={{
                    left: `${rawMousePosition.x}%`,
                    top: `${rawMousePosition.y}%`,
                    transform: 'translate(-50%, -50%)',
                    backgroundImage: `url(${currentImage})`,
                    backgroundSize: getMagnifyBackgroundSize(),
                    backgroundPosition: getMagnifyBackgroundPosition(),
                    backgroundRepeat: 'no-repeat',
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.3)',
                  }}
                >
                  {/* Inner highlight ring for professional look */}
                  <div className="absolute inset-2 rounded-full border border-white/40" />
                  {/* Center crosshair to show cursor position */}
                  <div className="absolute top-1/2 left-1/2 w-4 h-4 border border-black/30 rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                  <div className="absolute top-1/2 left-1/2 w-0.5 h-4 bg-black/30 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                  <div className="absolute top-1/2 left-1/2 w-4 h-0.5 bg-black/30 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              )}

              {/* Frame Content */}
              <div className="absolute inset-[20%] z-0 overflow-hidden">
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt={`${selectedStyle} style`}
                    className={`absolute inset-0 w-full h-full object-contain transition-all duration-500 transform select-none pointer-events-none ${
                      selectedSize === 'XS'
                        ? 'scale-[0.45] md:scale-[0.48] lg:scale-[0.50] xl:scale-[0.55]'
                        : selectedSize === 'S' 
                        ? 'scale-[0.55] md:scale-[0.6] lg:scale-[0.65] xl:scale-[0.75]'
                        : selectedSize === 'M'
                        ? 'scale-[0.7] md:scale-[0.75] lg:scale-[0.765] xl:scale-[0.855]'
                        : 'scale-[0.85] md:scale-[0.9] lg:scale-[0.95] xl:scale-[1.0]'
                    }`}
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                ) : (
                  <div className={`absolute inset-0 bg-gray-200 flex items-center justify-center transition-all duration-500 transform ${
                    selectedSize === 'XS'
                      ? 'scale-[0.45] md:scale-[0.48] lg:scale-[0.50] xl:scale-[0.55]'
                      : selectedSize === 'S' 
                      ? 'scale-[0.55] md:scale-[0.6] lg:scale-[0.65] xl:scale-[0.75]'
                      : selectedSize === 'M'
                      ? 'scale-[0.7] md:scale-[0.75] lg:scale-[0.765] xl:scale-[0.855]'
                      : 'scale-[0.85] md:scale-[0.9] lg:scale-[0.95] xl:scale-[1.0]'
                  }`}>
                    <span className="text-gray-400 text-sm">No Image Available</span>
                  </div>
                )}
              </div>
              <div className="absolute inset-0 bg-no-repeat">
                <img
                  src={getFrameUrl(selectedFrame)}
                  alt={`${selectedFrame} Frame`}
                  className={`absolute inset-0 w-full h-full object-contain z-10 transition-all duration-500 transform select-none pointer-events-none ${
                    selectedSize === 'XS'
                      ? 'scale-[0.45] md:scale-[0.48] lg:scale-[0.50] xl:scale-[0.55]'
                      : selectedSize === 'S' 
                      ? 'scale-[0.55] md:scale-[0.6] lg:scale-[0.65] xl:scale-[0.75]'
                      : selectedSize === 'M'
                      ? 'scale-[0.7] md:scale-[0.75] lg:scale-[0.765] xl:scale-[0.855]'
                      : 'scale-[0.85] md:scale-[0.9] lg:scale-[0.95] xl:scale-[1.0]'
                  }`}
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Order Form Modal - Rendered at the highest z-index level */}
      {showOrderForm && (
        <OrderForm onClose={() => setShowOrderForm(false)} />
      )}

      {/* Fullscreen Image Viewer */}
      {showFullscreen && currentImage && (
        <div className="fixed inset-0 z-[9999] bg-[#F5ECE1] overflow-hidden">
          {/* Close button */}
          <button
            onClick={() => {
              // Track fullscreen close in widget
              window.trackInteraction?.('fullscreen_toggled', {
                action: 'closed',
                current_style: selectedStyle,
                current_frame: selectedFrame,
                mode: selectedMode
              });
              setShowFullscreen(false);
            }}
            className="fixed top-4 right-4 z-[10000] p-2 bg-white/80 rounded-full hover:bg-white shadow-lg transition-all"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
          
          {/* Draggable image */}
          <motion.div
            className="absolute cursor-grab active:cursor-grabbing shadow-2xl"
            style={{
              left: '50%',
              top: '50%',
              marginLeft: '-880px', // Original container width / 2 (1760px / 2)
              marginTop: '-612px',  // Original container height / 2 (1224px / 2)
              width: '1760px',      // Fixed container width (1600px + 160px margin)
              height: '1224px',     // Fixed container height (1024px + 160px margin)
              backgroundImage: `url(${getTextureUrl(selectedStyle)})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundBlendMode: getTextureBlendMode(selectedStyle)
            }}
            initial={{
              scale: 0.1,
              x: frameCenter.x - window.innerWidth / 2,
              y: frameCenter.y - window.innerHeight / 2
            }}
            animate={{
              scale: dynamicScale,
              x: 0,
              y: 0
            }}
            transition={{
              duration: 1.5,
              ease: "easeOut"
            }}
            drag
            dragMomentum={false}
          >
            <img
              src={currentImage}
              alt="Fullscreen view"
              className="w-auto h-auto max-w-none block select-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ 
                width: '1600px',  // Fixed image width at 100% scale
                height: 'auto',
                margin: '80px',
                mixBlendMode: 'multiply',
                filter: getFullscreenFilters(selectedStyle),
                position: 'relative',
                zIndex: 1
              }}
              draggable={false}
            />
            {/* 100% Transparent overlay - physically blocks all image interactions */}
            <div 
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ 
                position: 'absolute',
                top: '80px',
                left: '80px',
                width: '1600px',
                height: 'calc(100% - 160px)',
                backgroundColor: 'transparent', // 100% transparent - invisible to users
                userSelect: 'none',
                WebkitUserSelect: 'none',
                msUserSelect: 'none',
                pointerEvents: 'auto',
                zIndex: 10,
                cursor: 'default'
              }}
            />
          </motion.div>
        </div>
      )}

      {/* Share Popup Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-[#F5ECE1] rounded-xl max-w-lg w-full p-6 relative shadow-2xl">
            {/* Close button */}
            <button
              onClick={() => setShowShareModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 pr-8">
                Share
              </h3>
              
              {/* Share Mode Toggle */}
              <div>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => {
                      setShareMode('social');
                      setCopySuccess(false);
                      setShareSuccess(false);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      shareMode === 'social'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    To Social
                  </button>
                  <button
                    onClick={() => {
                      setShareMode('copy');
                      setCopySuccess(false);
                      setShareSuccess(false);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      shareMode === 'copy'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Copy Link
                  </button>
                </div>
              </div>
              
              {/* Social Media Preview */}
              {shareThumbnail && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    {/* Thumbnail Image */}
                    <div className="aspect-[1.91/1] bg-gray-100 relative overflow-hidden">
                      <img 
                        src={shareThumbnail} 
                        alt="Social media preview" 
                        className="w-full h-full object-cover"
                      />
                      {/* Frame overlay for preview */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"></div>
                    </div>
                    
                    {/* Preview Text Content */}
                    <div className="p-3">
                      <div className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                        {shareMode === 'social' ? (
                          // Social mode: always show personalized message
                          shareMessage ? shareMessage.split('\n')[0] : 'Frame My Home'
                        ) : (
                          // Copy Link mode: show formal address format
                          (shareAddressLine1 && shareAddressLine1.toLowerCase() !== 'property') 
                            ? `${shareAddressLine1} | Beautifully Framed Artwork`
                            : 'Beautifully Framed Artwork'
                        )}
                      </div>
                      {shareMode === 'copy' && (
                      <div className="text-xs text-gray-600 line-clamp-2 mb-1">
                        {shareCity ? (
                          `Check out this special ${shareCity} home transformed into a stunning piece of AI art.`
                          ) : (shareAddressLine1 && shareAddressLine1.toLowerCase() !== 'property') ? (
                          'Check out this special home transformed into a stunning piece of AI art.'
                        ) : (
                          'Check out this stunning piece of AI art, beautifully framed and ready to hang.'
                        )}
                      </div>
                      )}
                      <div className="text-xs text-gray-400 uppercase tracking-wide">
                        framemyhome.ai
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Conditional Content Based on Share Mode */}
              {shareMode === 'social' ? (
                // Message Input for Social Sharing
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Personalize your message before sharing
                  </p>
                  <textarea
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    rows={2}
                    placeholder="Add your personal message here..."
                  />
                </div>
              ) : (
                // URL Display for Copy Link
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Copy this link to share anywhere
                  </p>
              <div className="bg-white border border-gray-200 rounded-lg p-3 break-all text-sm font-mono text-gray-700">
                {shareUrl}
              </div>
                </div>
              )}
              
              {/* Action Button */}
              <button
                onClick={shareMode === 'social' ? handleNativeShare : handleCopyUrl}
                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                  (shareMode === 'social' ? shareSuccess : copySuccess)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
                disabled={shareMode === 'social' ? shareSuccess : copySuccess}
              >
                {shareMode === 'social' ? (
                  shareSuccess ? (
                  <>
                    <Check className="w-4 h-4" />
                      Shared!
                    </>
                  ) : (
                    <>
                      <Share className="w-4 h-4" />
                      Share to Social
                    </>
                  )
                ) : (
                  copySuccess ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                      Copy Link
                  </>
                  )
                )}
              </button>
              
              {/* Social Media Icons - only show for social mode */}
              {shareMode === 'social' && (
                <div className="flex items-center justify-center gap-3 py-2">
                  <Facebook className="w-5 h-5 text-gray-500" />
                  <Instagram className="w-5 h-5 text-gray-500" />
                  <MessageCircle className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Welcome Overlay */}
      <WelcomeOverlay
        isVisible={showWelcomeOverlay}
        onConfirm={handleWelcomeOverlayConfirm}
        watercolorImage={currentWatercolorImage}
        address={currentAddress}
      />
      </div> {/* End Main Content Container */}
    </div>
  );
};

export default Configurator;